import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';

import Search, { SearchEvent } from 'carbon-react/lib/components/search';

import { ROUTES, useWorkspaceOrganisationSelector } from '@futrli/shared-services';

import { AccountListFr } from './components/account-list-fr/account-list-fr';
import { BasicInfo } from './components/basic-info/basic-info';
import { COADisplayAccount } from './types';
import { getAccountName } from './utils';
import { normalizeStr } from '../utils/text';
import { Tabs, TABS_HEIGHT } from './components/tabs/tabs';
import { useNavHeight } from '../hooks/useNavHeight';

import * as styles from './chart-of-accounts-container.scss';

interface Props {
  accounts: COADisplayAccount[];
  displayAccountCodes?: boolean;
}

export const ChartOfAccountsContainerFr = ({ accounts, displayAccountCodes }: Props) => {
  const { organisationId: tempOrganisationId, workspaceId: tempWorkspaceId } = useParams();
  const organisationId = tempOrganisationId || '';
  const workspaceId = tempWorkspaceId || '';
  const organisation = useWorkspaceOrganisationSelector(workspaceId, organisationId);

  const intl = useIntl();
  const { navHeight } = useNavHeight();
  const { pathname } = useLocation();
  const isBalanceSheet = pathname.indexOf(ROUTES.BALANCE_SHEET) !== -1;

  const [searchText, setSearchText] = useState('');

  const handleSearchChange = useCallback((e: SearchEvent) => {
    setSearchText(e.target.value);
  }, []);

  const accountsForDisplay = useMemo(() => {
    if (!searchText) {
      return accounts;
    }

    return accounts.filter(account =>
      normalizeStr(getAccountName(account, displayAccountCodes)).includes(normalizeStr(searchText))
    );
  }, [accounts, searchText, displayAccountCodes]);

  return (
    <div className={styles.content}>
      <BasicInfo />
      <Tabs />
      <div className={styles.actionsContainer} style={{ top: navHeight + TABS_HEIGHT }}>
        <Search
          id="search-accounts"
          label={intl.formatMessage({
            id: 'common.search_ellipsis',
            defaultMessage: 'Search...'
          })}
          onChange={handleSearchChange}
          value={searchText}
          inputWidth={100}
        />
      </div>
      <div data-testid="coa-categories-container">
        <AccountListFr
          isBalanceSheet={isBalanceSheet}
          syncStatusModified={organisation?.datasource?.sync_status_modified}
          tableData={accountsForDisplay}
          displayAccountCodes={displayAccountCodes}
        />
      </div>
    </div>
  );
};
